/***************************************************************************
 * SavedRowOverlay — a single "Saved" badge portaled into the grid root and
 * absolutely positioned at the right edge of the row that was most recently
 * saved.  Lingers for SAVE_SUCCESS_BANNER_MS (controlled by useRowEditor)
 * so the user gets a clear confirmation even if they immediately move on
 * to hover / edit other rows.
 *
 * This sits alongside HoverRowActionsOverlay at the same right edge.  The
 * hover overlay suppresses its own render when targetRowId matches
 * savedRowId so the two don't fight for the same pixel space.
 ***************************************************************************/

import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {createPortal} from "react-dom";
import Box from "@mui/material/Box";
import {GridApi} from "@mui/x-data-grid-pro";
import {useRowEditContext} from "./RowEditContext";

interface Props
{
   apiRef: React.MutableRefObject<GridApi>;
   /** Clicking the Saved badge re-enters edit on that same row. */
   onEnterEdit: (rowId: string | number) => void;
}

export const SavedRowOverlay: React.FC<Props> = ({apiRef, onEnterEdit}) =>
{
   const ctx = useRowEditContext();
   const savedRowId = ctx?.savedRowId ?? null;

   const [rootEl, setRootEl] = useState<HTMLElement | null>(null);
   const [style, setStyle] = useState<React.CSSProperties>({display: "none"});

   ///////////////////////////////////////////////////////////////////////////////////
   // Resolve the grid root once it's mounted (DataGridPro populates rootElementRef  //
   // during its first layout effect which may run after our own mount).             //
   ///////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      let cancelled = false;
      function tryResolve()
      {
         const root = apiRef.current?.rootElementRef?.current as HTMLElement | undefined;
         if (root)
         {
            setRootEl(root);
            return true;
         }
         return false;
      }
      if (!tryResolve())
      {
         const id = setInterval(() =>
         {
            if (cancelled)
            {
               return;
            }
            if (tryResolve())
            {
               clearInterval(id);
            }
         }, 50);
         return () =>
         {
            cancelled = true;
            clearInterval(id);
         };
      }
   }, [apiRef]);

   const updateStyle = useCallback(() =>
   {
      if (!rootEl || savedRowId == null)
      {
         setStyle({display: "none"});
         return;
      }
      const rowEl = apiRef.current?.getRowElement?.(savedRowId) as HTMLElement | null;
      if (!rowEl)
      {
         setStyle({display: "none"});
         return;
      }
      const rootRect = rootEl.getBoundingClientRect();
      const rowRect  = rowEl.getBoundingClientRect();
      const top      = rowRect.top - rootRect.top;
      const height   = rowRect.height;

      /////////////////////////////////////////////////////////////////////////////////
      // Hide when the saved row has been scrolled out of the visible viewport, same //
      // logic the hover overlay uses for its row.                                   //
      /////////////////////////////////////////////////////////////////////////////////
      const scroller = rootEl.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null;
      const viewport = scroller ? scroller.getBoundingClientRect() : rootRect;
      if (rowRect.top + rowRect.height <= viewport.top || rowRect.top >= viewport.bottom)
      {
         setStyle({display: "none"});
         return;
      }

      setStyle({
         position:      "absolute",
         top,
         height,
         right:         20,
         display:       "flex",
         alignItems:    "center",
         ///////////////////////////////////////////////////////////////////////////////
         // Low z-index so the grid's header / footer chrome cover the badge when the //
         // row is scrolled partially behind them — matches the hover overlay.        //
         ///////////////////////////////////////////////////////////////////////////////
         zIndex:        1,
         pointerEvents: "none",
      });
   }, [apiRef, rootEl, savedRowId]);

   useLayoutEffect(() =>
   {
      updateStyle();
   }, [updateStyle]);

   useEffect(() =>
   {
      if (!rootEl)
      {
         return;
      }
      const ro = new ResizeObserver(updateStyle);
      ro.observe(rootEl);
      const scroller = rootEl.querySelector(".MuiDataGrid-virtualScroller") as HTMLElement | null;
      if (scroller)
      {
         scroller.addEventListener("scroll", updateStyle);
      }
      window.addEventListener("resize", updateStyle);
      return () =>
      {
         ro.disconnect();
         if (scroller)
         {
            scroller.removeEventListener("scroll", updateStyle);
         }
         window.removeEventListener("resize", updateStyle);
      };
   }, [rootEl, updateStyle]);

   const content = useMemo(() =>
   {
      if (savedRowId == null)
      {
         return null;
      }
      return (
         <div style={style} className="qqq-row-edit-saved-overlay">
            <Box
               className="qqq-row-edit-saved-indicator"
               role="button"
               tabIndex={0}
               title="Click to edit again"
               onClick={(e) =>
               {
                  e.stopPropagation();
                  onEnterEdit(savedRowId);
               }}
               sx={{
                  color:           "success.main",
                  fontWeight:      600,
                  fontSize:        "0.875rem",
                  backgroundColor: "#ffffff",
                  borderRadius:    "6px",
                  boxShadow:       "0 1px 4px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)",
                  padding:         "4px 14px",
                  whiteSpace:      "nowrap",
                  cursor:          "pointer",
                  pointerEvents:   "auto",
                  "&:hover":       {boxShadow: "0 2px 6px rgba(0, 0, 0, 0.18), 0 1px 2px rgba(0, 0, 0, 0.10)"},
               }}
            >
               Saved
            </Box>
         </div>
      );
   }, [savedRowId, style, onEnterEdit]);

   if (!rootEl || !content)
   {
      return null;
   }
   return createPortal(content, rootEl);
};
